import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { consoleApi } from '@/lib/consoleApi';
import {
  computePass, collectDisagreements, scoresComplete, noteRequired,
  type Attribution, type PeriReviewRubric, type Scores, type PeriReviewQueueRow,
  type PeriReviewInputs,
} from '@/lib/periReview';
import { AdminPage } from '@/admin';

function isRecordArray(v: unknown): v is unknown[] { return Array.isArray(v); }

/** The as-of-run-time raw inputs the blinded advisor reads before the board
 *  output is revealed — profile biomarkers, symptom/instrument captures, and
 *  the intake/safety record, as the board saw them at run time. */
function RawInputsView({ inputs }: { inputs: PeriReviewInputs }) {
  const safety = inputs.intake_safety ?? {};
  const listOf = (k: string) => (isRecordArray(safety[k]) ? (safety[k] as unknown[]) : []);
  const conditions = listOf('conditions');
  const medications = listOf('medications');
  const allergies = listOf('allergies');
  const family = listOf('family_history');
  const screenings = (safety.screenings ?? {}) as Record<string, unknown>;
  return (
    <div className="space-y-13 text-body-sm">
      <p className="text-[12px] text-text-muted">
        As of run time · {new Date(inputs.run_started_at).toLocaleString()} · profile v{inputs.profile_version ?? '—'}
      </p>

      <div>
        <div className="text-[12px] font-semibold uppercase text-text-muted mb-1">Biomarkers</div>
        {inputs.biomarker_histories.length ? (
          <ul className="space-y-0.5">
            {inputs.biomarker_histories.map((b) => {
              const last = b.readings[b.readings.length - 1];
              return (
                <li key={b.name} className="text-[12px]">
                  <span className="font-mono">{b.name}</span>{' '}
                  {/* value already embeds the unit (e.g. "5.2 mmol/L") — don't re-append b.unit */}
                  <span className="text-text-secondary">{last ? String(last.value) : 'no reading'}</span>
                  <span className="text-text-muted"> · {b.readings.length} reading{b.readings.length === 1 ? '' : 's'}</span>
                </li>
              );
            })}
          </ul>
        ) : <p className="text-[12px] text-text-secondary">No biomarker histories.</p>}
      </div>

      <div>
        <div className="text-[12px] font-semibold uppercase text-text-muted mb-1">Symptom / instrument captures</div>
        {inputs.instrument_captures.length ? (
          <ul className="space-y-0.5">
            {inputs.instrument_captures.map((c) => (
              <li key={c.instrument_id} className="text-[12px]">
                <span className="font-mono">{c.instrument_id}</span>
                <span className="text-text-muted"> · {c.records.length} record{c.records.length === 1 ? '' : 's'}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-[12px] text-text-secondary">No captures.</p>}
      </div>

      <div>
        <div className="text-[12px] font-semibold uppercase text-text-muted mb-1">Intake / safety</div>
        <div className="text-[12px] space-y-0.5 text-text-secondary">
          <div><span className="text-text-primary">Conditions:</span> {conditions.length || '—'}</div>
          <div><span className="text-text-primary">Medications:</span> {medications.length || '—'}</div>
          <div><span className="text-text-primary">Allergies:</span> {allergies.length || '—'}</div>
          <div><span className="text-text-primary">Family history:</span> {family.length || '—'}</div>
          <div><span className="text-text-primary">Screenings:</span> PHQ-8 {String(screenings.phq9 ?? '—')} · GAD-7 {String(screenings.gad7 ?? '—')}</div>
          <div><span className="text-text-primary">HRT status:</span> {String(safety.hrt_status ?? '—')} · <span className="text-text-primary">uterine:</span> {String(safety.uterine_status ?? '—')} · <span className="text-text-primary">ND:</span> {String(safety.neurodivergence ?? '—')}</div>
        </div>
      </div>
    </div>
  );
}

function ImpressionForm({ participantRef, sessionId }: { participantRef: string; sessionId: string }) {
  const qc = useQueryClient();
  const [impression, setImpression] = useState('');
  const [signatory, setSignatory] = useState('');
  const submit = useMutation({
    mutationFn: () => consoleApi.periReview.submitImpression(participantRef, {
      session_id: sessionId, impression, signatory,
    }),
    // On success the reveal gate opens — refetch the queue so status flips to
    // impression_recorded and the board output + rubric become available.
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['peri-review-queue'] }); },
  });
  const ready = impression.trim().length > 0 && signatory.trim().length > 0;
  return (
    <div className="space-y-2">
      <p className="text-[12px] text-text-secondary">
        Record your independent impression from the raw inputs alone. The board output stays withheld until you submit.
      </p>
      <textarea
        className="w-full rounded-md border border-border bg-surface px-2 py-1 text-body-sm" rows={4}
        placeholder="Your independent read of this participant…"
        value={impression} onChange={(e) => setImpression(e.target.value)}
      />
      <input
        className="w-full rounded-md border border-border bg-surface px-2 py-1 text-body-sm"
        placeholder="Signatory (name + credentials)"
        value={signatory} onChange={(e) => setSignatory(e.target.value)}
      />
      {submit.isError ? <p className="text-body-sm text-flagged">{submit.error instanceof Error ? submit.error.message : 'Submit failed'}</p> : null}
      <Button size="sm" disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
        {submit.isPending ? 'Recording…' : 'Record impression & reveal output'}
      </Button>
    </div>
  );
}

function AttributionView({ a }: { a: Attribution | null }) {
  if (!a) return <p className="text-body-sm text-text-secondary">No attribution artifact for this session.</p>;
  const ruleOuts = a.hypotheses.filter((h) => h.stance === 'ruled_out');
  const positive = a.hypotheses.filter((h) => h.stance !== 'ruled_out');
  return (
    <div className="space-y-13">
      {a.question ? <p className="text-body-sm text-text-secondary">{a.question}</p> : null}
      {typeof a.overall_confidence === 'number' ? (
        <p className="text-body-sm"><strong>Overall confidence:</strong> {a.overall_confidence}%{a.confidence_rationale ? ` — ${a.confidence_rationale}` : ''}</p>
      ) : null}
      {a.staging ? (
        <p className="text-body-sm"><strong>STRAW+10 staging:</strong> {a.staging.stage_label ?? a.staging.stage ?? '—'}
          {typeof a.staging.stage_confidence === 'number' ? ` (${a.staging.stage_confidence}%)` : ''}
          {a.staging.basis?.length ? ` — ${a.staging.basis.join('; ')}` : ''}</p>
      ) : null}
      <div>
        <div className="text-[12px] font-semibold uppercase text-text-muted mb-1">Differential</div>
        {positive.map((h, i) => (
          <div key={i} className="mb-2 text-body-sm">
            <span className="font-semibold">{h.label}</span> · <span className="text-text-secondary">{h.stance} · {h.confidence}%</span>
            {h.reasoning ? <div className="text-text-secondary">{h.reasoning}</div> : null}
          </div>
        ))}
      </div>
      {ruleOuts.length ? (
        <div>
          <div className="text-[12px] font-semibold uppercase text-text-muted mb-1">What it's not (ruled out)</div>
          {ruleOuts.map((h, i) => (
            <div key={i} className="mb-2 text-body-sm">
              <span className="font-semibold">{h.label}</span>
              {h.reasoning ? <span className="text-text-secondary"> — {h.reasoning}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ScoringForm({
  rubric, row, sessionId,
}: { rubric: PeriReviewRubric; row: PeriReviewQueueRow; sessionId: string }) {
  const qc = useQueryClient();
  const [scores, setScores] = useState<Scores>({});
  const [signatory, setSignatory] = useState('');
  const [done, setDone] = useState(false);

  const setValue = (cid: string, value: string) => setScores((s) => ({ ...s, [cid]: { value, note: s[cid]?.note ?? '' } }));
  const setNote = (cid: string, note: string) => setScores((s) => ({ ...s, [cid]: { value: s[cid]?.value ?? '', note } }));

  const complete = scoresComplete(rubric, scores) && signatory.trim().length > 0;
  const pass = complete ? computePass(rubric, scores) : null;
  const disagreements = collectDisagreements(rubric, scores);

  const submit = useMutation({
    mutationFn: () => consoleApi.periReview.submitVerdict(row.participant_ref, {
      session_id: sessionId, review_mode: row.review_mode, scores, signatory,
    }),
    onSuccess: () => { setDone(true); void qc.invalidateQueries({ queryKey: ['peri-review-queue'] }); },
  });

  if (done) return <p className="text-body-sm font-semibold">Verdict recorded. Thank you.</p>;

  return (
    <div className="space-y-4">
      {rubric.categories.map((cat) => {
        const entry = scores[cat.id];
        const needsNote = noteRequired(cat, entry?.value);
        return (
          <div key={cat.id} className="border-t border-border pt-3">
            <div className="text-body-sm font-semibold">{cat.label}{cat.gating ? <span className="ml-2 text-flagged text-[11px]">gating</span> : null}</div>
            <div className="text-[12px] text-text-secondary mb-2">{cat.prompt}</div>
            <select
              className="rounded-md border border-border bg-surface px-2 py-1 text-body-sm"
              value={entry?.value ?? ''}
              onChange={(e) => setValue(cat.id, e.target.value)}
            >
              <option value="" disabled>Score…</option>
              {cat.scale.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            {entry?.value && needsNote ? (
              <textarea
                className="mt-2 w-full rounded-md border border-border bg-surface px-2 py-1 text-body-sm"
                placeholder="Note required for this score…" rows={2}
                value={entry.note ?? ''} onChange={(e) => setNote(cat.id, e.target.value)}
              />
            ) : null}
          </div>
        );
      })}

      <div className="border-t border-border pt-3">
        <label className="block text-[12px] text-text-secondary mb-1">Signatory (name + credentials)</label>
        <input
          className="w-full rounded-md border border-border bg-surface px-2 py-1 text-body-sm"
          placeholder="e.g. Dr Jane Smith, FRACGP"
          value={signatory} onChange={(e) => setSignatory(e.target.value)}
        />
      </div>

      {complete ? (
        <div className="text-body-sm">
          <div>Primary metric: {pass ? <span className="font-semibold">PASS</span> : <span className="text-flagged font-semibold">does not pass</span>}</div>
          {disagreements.length ? (
            <div className="text-text-secondary">Disagreement catalogue: {disagreements.map((d) => d.category).join(', ')}</div>
          ) : <div className="text-text-secondary">No disagreements.</div>}
        </div>
      ) : null}

      {submit.isError ? <p className="text-body-sm text-flagged">{submit.error instanceof Error ? submit.error.message : 'Submit failed'}</p> : null}
      <Button size="sm" disabled={!complete || submit.isPending} onClick={() => submit.mutate()}>
        {submit.isPending ? 'Recording…' : 'Record verdict'}
      </Button>
    </div>
  );
}

export function PeriReviewDetailPage() {
  const { participantRef = '' } = useParams();
  const queue = useQuery({ queryKey: ['peri-review-queue'], queryFn: () => consoleApi.periReview.queue() });
  const rubricQ = useQuery({ queryKey: ['peri-review-rubric'], queryFn: () => consoleApi.periReview.rubric() });

  const row = queue.data?.participants.find((p) => p.participant_ref === participantRef);
  const sessionId = row?.latest_session_id ?? null;
  const isBlinded = row?.review_mode === 'blinded';
  const impressionIn = row?.review_status === 'impression_recorded' || row?.review_status === 'verdict_recorded';
  // Blinded participants must record a phase-1 impression before the board output is revealed.
  const outputAllowed = !!sessionId && (!isBlinded || impressionIn);

  const output = useQuery({
    queryKey: ['peri-review-output', participantRef, sessionId],
    queryFn: () => consoleApi.periReview.output(participantRef, sessionId as string),
    enabled: outputAllowed,
  });

  // Blinded phase-1: fetch the raw inputs (always allowed) for the independent read.
  const inputs = useQuery({
    queryKey: ['peri-review-inputs', participantRef, sessionId],
    queryFn: () => consoleApi.periReview.inputs(participantRef, sessionId as string),
    enabled: !!sessionId && isBlinded && !impressionIn,
  });

  return (
    <AdminPage title={`Review · ${participantRef}`} backTo={{ to: '/review/peri', label: 'Perimenopause review' }}>
      {queue.isLoading || rubricQ.isLoading ? <p className="text-body-sm text-text-secondary">Loading…</p> : null}
      {!row && !queue.isLoading ? <p className="text-body-sm text-flagged">Participant not in the cohort.</p> : null}

      {row ? (
        <div className="space-y-6">
          <div className="text-body-sm text-text-secondary">
            Mode <strong className="text-text-primary">{row.review_mode}</strong> · strata {row.strata_stage} / {row.strata_subbranch}
            {sessionId ? <> · session <span className="font-mono text-[12px]">{sessionId}</span></> : ' · no board session yet'}
          </div>

          {isBlinded && !impressionIn ? (
            <section className="space-y-4">
              <div className="rounded-md border border-border bg-surface-sunken p-4">
                <div className="text-body-sm font-semibold mb-1">Blinded review — phase 1 (independent read)</div>
                <p className="text-[12px] text-text-secondary">
                  The board output is withheld. Read the raw inputs, record your own impression, then the output
                  is revealed for phase-2 rubric scoring.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h3 className="text-body-sm font-semibold uppercase text-text-muted mb-2">Raw inputs (as-of-run-time)</h3>
                  {inputs.isLoading ? <p className="text-body-sm text-text-secondary">Loading inputs…</p> : null}
                  {inputs.isError ? <p className="text-body-sm text-flagged">Could not load raw inputs.</p> : null}
                  {inputs.data ? <RawInputsView inputs={inputs.data} /> : null}
                </div>
                <div>
                  <h3 className="text-body-sm font-semibold uppercase text-text-muted mb-2">Your independent impression</h3>
                  {sessionId ? <ImpressionForm participantRef={participantRef} sessionId={sessionId} /> : <p className="text-body-sm text-text-secondary">No board session to review.</p>}
                </div>
              </div>
            </section>
          ) : (
            <>
              <section>
                <h3 className="text-body-sm font-semibold uppercase text-text-muted mb-2">Board output</h3>
                {output.isLoading ? <p className="text-body-sm text-text-secondary">Loading output…</p> : null}
                {output.isError ? <p className="text-body-sm text-flagged">Could not load board output.</p> : null}
                {output.data ? (
                  <>
                    <AttributionView a={output.data.attribution} />
                    {output.data.contraindication_flags?.length ? (
                      <p className="mt-3 text-body-sm"><strong>Contraindication flags:</strong> {output.data.contraindication_flags.length}</p>
                    ) : null}
                    <p className="mt-2 text-[12px] text-text-muted">
                      Mode {output.data.mode_config.mode} · {output.data.mode_config.sub_branch}
                    </p>
                  </>
                ) : null}
              </section>

              <section>
                <h3 className="text-body-sm font-semibold uppercase text-text-muted mb-2">Rubric</h3>
                {rubricQ.data && sessionId ? (
                  <ScoringForm rubric={rubricQ.data} row={row} sessionId={sessionId} />
                ) : <p className="text-body-sm text-text-secondary">No rubric or session available.</p>}
              </section>
            </>
          )}
        </div>
      ) : null}
    </AdminPage>
  );
}
